import React, { useEffect } from 'react';
import { Callout, EmptyState } from '@neshane/blueberry';
import { useToast } from '../../hooks';
import { useQueryError } from './useQueryError';

/**
 * QueryError component
 *
 * Renders a `QueryError` (see `src/api/queryError.js`) as one of several UI
 * presentations selected through `componentType`:
 *  - 'Callout'      -> `Callout` (variant="alert", triangle-warning icon)
 *  - 'ErrorState'   -> `EmptyState` (warning icon, like the legacy ErrorState)
 *  - 'Text'         -> a plain `<span>`
 *  - 'Notification' -> pushes an error toast through the app's toast system
 *
 * @param {string} [className] - Extra class name forwarded to the rendered element.
 * @param {'Callout'|'ErrorState'|'Text'|'Notification'} componentType - Presentation mode.
 * @param {object} queryError - The QueryError to render.
 * @param {object} [logContext] - When provided the error is logged to the console.
 */
function QueryError({
    className,
    componentType,
    queryError,
    logContext,
}) {
    const { error: showErrorToast } = useToast();

    const errorTitle = useQueryError()(queryError);

    useEffect(() => {
        if (componentType === 'Notification') {
            showErrorToast(errorTitle);
        }
    }, [componentType, errorTitle, showErrorToast]);

    useEffect(() => {
        if (logContext) {
            console.error(
                `[QueryError] ${queryError.name ? `${queryError.name} ` : ''}${
                    queryError.message
                }`,
                { ...queryError, ...logContext }
            );
        }
    }, [queryError, logContext]);

    switch (componentType) {
        case 'Callout':
            return (
                <Callout
                    variant="alert"
                    title={errorTitle}
                    iconName="triangle-warning"
                    className={className}
                />
            );
        case 'ErrorState':
            return (
                <EmptyState
                    className={className}
                    title={errorTitle}
                    subtitle="اگر مشکل همچنان ادامه دارد، لطفاً با پشتیبانی تماس بگیرید."
                    iconName="triangle-warning"
                    iconVariant="warning"
                />
            );
        case 'Text':
            return <span className={className}>{errorTitle}</span>;
        default:
            return null;
    }
}

export default QueryError;